/* THE DOGS · Shared product-gallery gestures and unobstructed navigation. */
(() => {
  'use strict';
  if (window.TDGalleryUX) return;

  const ROOTS = '#td-rampa-editorial,#td-majestuoso-editorial,#funda-editorial,#td-nails,#td-cama,#td-elevada,#td-comedero,#td-asiento,#td-nido,#td-piel-editorial,#td-luxury-editorial,#td-impermeable-editorial';
  const STAGES = '.gallery-stage,.hero-media,.gallery-shell';
  const controllers = new Map();
  const activePointers = new Set();
  let gesture = null;
  let lastSwipe = null;

  const element = target => target instanceof Element ? target : target?.parentElement;
  const controllerFor = target => {
    const stage = element(target)?.closest('[data-td-gallery-stage]');
    return stage ? controllers.get(stage) : null;
  };
  const swipeSurfaceButton = button => button?.matches('#image-open,#zoom-image,[data-td-image-zoom],#video-play,#gallery-video-play');
  const blockedTarget = (target, point) => {
    const node = element(target);
    if (!node) return true;
    const control = node.closest('button,a,input,select,textarea,[role="slider"]');
    if (control && !swipeSurfaceButton(control)) return true;
    const video = node.closest('video');
    // Native playback controls remain usable, including dragging the scrubber.
    if (video?.controls && point.clientY >= video.getBoundingClientRect().bottom - 52) return true;
    return false;
  };
  const swipeDirection = (start, end) => {
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    if (Math.abs(dx) < 28 || Math.abs(dx) < Math.abs(dy) * 1.2) return 0;
    return dx < 0 ? 1 : -1;
  };
  const consume = event => {
    // Prevent the old per-product gesture handler from moving a second slide.
    event.stopImmediatePropagation();
    if (event.cancelable) event.preventDefault();
  };
  const finishGesture = event => {
    const current = gesture;
    gesture = null;
    if (!current || !current.controller.stage.isConnected) return false;
    const direction = swipeDirection(current, event);
    if (!direction || !current.controller.move(direction)) return false;
    lastSwipe = { controller: current.controller, time: Date.now() };
    consume(event);
    return true;
  };

  const capture = { capture: true, passive: true };
  document.addEventListener('pointerdown', event => {
    if (event.pointerType === 'mouse' || event.button > 0) return;
    activePointers.add(event.pointerId);
    if (activePointers.size > 1) { gesture = null; return; }
    // A new intentional touch is not the synthetic click from the last swipe.
    lastSwipe = null;
    const controller = controllerFor(event.target);
    gesture = controller && !blockedTarget(event.target, event)
      ? { controller, pointerId: event.pointerId, x: event.clientX, y: event.clientY }
      : null;
  }, capture);
  document.addEventListener('pointerup', event => {
    activePointers.delete(event.pointerId);
    if (gesture?.pointerId !== event.pointerId) return;
    finishGesture(event);
  }, { capture: true, passive: false });
  document.addEventListener('pointercancel', event => {
    activePointers.delete(event.pointerId);
    if (gesture?.pointerId === event.pointerId) gesture = null;
  }, capture);

  // Touch fallback covers older WebViews. Modern browsers use Pointer Events;
  // their following touchend must not activate a second legacy swipe handler.
  document.addEventListener('touchstart', event => {
    if (event.touches.length !== 1) { gesture = null; return; }
    if ('PointerEvent' in window) return;
    lastSwipe = null;
    const point = event.touches[0];
    const controller = controllerFor(event.target);
    gesture = controller && !blockedTarget(event.target, point)
      ? { controller, x: point.clientX, y: point.clientY }
      : null;
  }, capture);
  document.addEventListener('touchend', event => {
    const controller = controllerFor(event.target);
    if (controller && lastSwipe?.controller === controller && Date.now() - lastSwipe.time < 250) {
      consume(event);
      return;
    }
    if ('PointerEvent' in window || event.touches.length || !event.changedTouches[0]) return;
    const point = event.changedTouches[0];
    finishGesture({
      clientX: point.clientX, clientY: point.clientY, cancelable: event.cancelable,
      stopImmediatePropagation: () => event.stopImmediatePropagation(),
      preventDefault: () => event.preventDefault()
    });
  }, { capture: true, passive: false });
  document.addEventListener('touchcancel', () => { gesture = null; activePointers.clear(); }, capture);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { gesture = null; activePointers.clear(); }
  });
  document.addEventListener('click', event => {
    const controller = controllerFor(event.target);
    if (event.detail !== 0 && controller && lastSwipe?.controller === controller && Date.now() - lastSwipe.time < 500) {
      consume(event);
    }
  }, { capture: true, passive: false });

  const makeArrow = (direction, label) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${direction < 0 ? 'M20 12H4m6-6-6 6 6 6' : 'M4 12h16m-6-6 6 6-6 6'}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    return button;
  };

  const initialize = root => {
    const stage = root.querySelector(STAGES);
    if (!stage || controllers.has(stage)) return;
    stage.dataset.tdGalleryStage = 'true';
    root.dataset.tdGalleryRoot = 'true';
    const previous = root.querySelector('#gallery-prev');
    const next = root.querySelector('#gallery-next');
    const controller = {
      root, stage,
      move(direction) {
        const button = direction > 0 ? next : previous;
        if (button) {
          if (button.disabled || button.getAttribute('aria-disabled') === 'true') return false;
          button.click();
        } else {
          // Nido uses its existing thumbnail selection as its source of truth.
          const thumbs = Array.from(root.querySelectorAll('[data-gallery-index]')).filter(item => !item.hidden && !item.disabled);
          if (thumbs.length < 2) return false;
          const current = Math.max(0, thumbs.findIndex(item => item.getAttribute('aria-pressed') === 'true' || item.classList.contains('is-active')));
          thumbs[(current + direction + thumbs.length) % thumbs.length].click();
        }
        stage.dispatchEvent(new CustomEvent('td:gallery-media-change', { bubbles: true }));
        return true;
      }
    };
    controllers.set(stage, controller);

    if ((previous && stage.contains(previous)) || (!previous && !next)) {
      const navigation = document.createElement('div');
      navigation.dataset.tdGalleryNav = 'true';
      navigation.setAttribute('role', 'group');
      navigation.setAttribute('aria-label', root.getAttribute('data-gallery-navigation-label') || 'Navegación de la galería');
      const counter = root.querySelector('.gallery-counter,#gallery-position,#gallery-counter')
        || root.querySelector('#gallery-current')?.parentElement;
      const prevButton = previous || makeArrow(-1, 'Ver imagen anterior');
      const nextButton = next || makeArrow(1, 'Ver imagen siguiente');
      if (!previous) prevButton.addEventListener('click', () => controller.move(-1));
      if (!next) nextButton.addEventListener('click', () => controller.move(1));
      navigation.append(prevButton);
      if (counter) navigation.append(counter);
      navigation.append(nextButton);
      stage.after(navigation);
      // Remove only empty decorative containers left by the moved controls.
      stage.querySelectorAll('.gallery-tools,.gallery-nav,.gallery-navigation').forEach(container => {
        if (!container.querySelector('button:not(.zoom-trigger),[id^="gallery-current"],[id^="gallery-counter"]')) container.hidden = true;
      });
    } else if (previous && next && previous.parentElement === next.parentElement) {
      previous.parentElement.dataset.tdGalleryNav = 'true';
    }

    // Keep the image itself as the zoom target; remove the separate expand icon.
    const existingImageButton = stage.querySelector('#image-open,#zoom-image');
    const zoomButton = root.querySelector('#gallery-zoom,.gallery-zoom,#zoom-button,.zoom-trigger');
    root.querySelectorAll('.image-expand').forEach(icon => { icon.dataset.tdZoomIcon = 'true'; });
    if (zoomButton && !existingImageButton && root.matches('#td-piel-editorial,#td-luxury-editorial,#td-impermeable-editorial')) {
      zoomButton.dataset.tdImageZoom = 'true';
      stage.append(zoomButton);
      const syncZoom = () => {
        const image = stage.querySelector('img');
        const available = Boolean(image && !image.closest('[hidden],.hidden'));
        const value = available ? 'true' : 'false';
        if (zoomButton.dataset.tdZoomAvailable !== value) zoomButton.dataset.tdZoomAvailable = value;
      };
      const observer = new MutationObserver(syncZoom);
      observer.observe(stage, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class'] });
      controller.observer = observer;
      syncZoom();
    } else if (zoomButton) {
      zoomButton.dataset.tdZoomIcon = 'true';
    }
  };
  const scan = scope => {
    if (scope.matches?.(ROOTS)) initialize(scope);
    scope.querySelectorAll?.(ROOTS).forEach(initialize);
  };
  const cleanup = scope => {
    controllers.forEach((controller, stage) => {
      if (scope.contains(stage)) { controller.observer?.disconnect(); controllers.delete(stage); }
    });
  };
  window.TDGalleryUX = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => cleanup(event.target));
})();
